import type { AmhsStocker, AmhsTool, AmhsTransportFlow } from '../../types/amhsSimulationRequest';
import { DEFAULT_PROCESS_TYPE } from '../equipment/equipmentConstants';
import { DEFAULT_FOUP_COUNT } from './amhsSimulationConfig';

/** Fallback only when layout JSON omits transportFlow settings. */
export const TRANSPORT_FLOW_DEFAULTS = {
  flowMode: 'stocker_buffered' as const,
  bayMode: 'browser_continuous' as const,
  layoutMode: 'hybrid_e2e' as const,
  foupReleaseMode: 'segment-free' as const,
};

export type BuildTransportFlowInput = {
  stockers: AmhsStocker[];
  tools: AmhsTool[];
  foupCount?: number;
  ohtCount: number;
  entryLandmarkId?: string;
  exitLandmarkId?: string;
  /** Layout JSON `metadata.transportFlow` + `metadata.inventory`. */
  layoutMetadata?: Record<string, unknown> | null;
};

/** Prefer a stocker tagged for this process family; else outside / first stocker. */
export function stockerIdForToolType(
  stockers: ReadonlyArray<Pick<AmhsStocker, 'stockerId' | 'name' | 'processType'>>,
  toolType: string,
  fallbackId: string
): string {
  const want = toolType.trim().toUpperCase();
  const tagged = stockers.find(
    (stocker) => String(stocker.processType || '').trim().toUpperCase() === want
  );
  if (tagged) return tagged.stockerId;

  const byName = stockers.find((stocker) =>
    String(stocker.name || '')
      .toUpperCase()
      .includes(want)
  );
  if (byName) return byName.stockerId;

  return fallbackId;
}

/**
 * 1-based bay index per tool within its toolType group (encounter order).
 * Load ports on the same tool share this index; stage `toolOrder` lists these values.
 */
export function buildProcessToolIndexByToolId(tools: AmhsTool[]): Map<string, number> {
  const toolsByType = new Map<string, AmhsTool[]>();
  for (const tool of tools) {
    const toolType = String(tool.toolType || '').trim().toUpperCase() || DEFAULT_PROCESS_TYPE;
    const group = toolsByType.get(toolType) ?? [];
    group.push(tool);
    toolsByType.set(toolType, group);
  }

  const indexById = new Map<string, number>();
  for (const group of toolsByType.values()) {
    group.forEach((tool, index) => {
      indexById.set(tool.toolId, index + 1);
    });
  }
  return indexById;
}

function groupToolsByType(tools: AmhsTool[]): Map<string, AmhsTool[]> {
  const toolsByType = new Map<string, AmhsTool[]>();
  for (const tool of tools) {
    const toolType = String(tool.toolType || '').trim().toUpperCase() || DEFAULT_PROCESS_TYPE;
    const group = toolsByType.get(toolType) ?? [];
    group.push(tool);
    toolsByType.set(toolType, group);
  }
  return toolsByType;
}

type FlowOverrides = {
  flowMode: NonNullable<AmhsTransportFlow['flowMode']>;
  bayMode: NonNullable<AmhsTransportFlow['bayMode']>;
  layoutMode: NonNullable<AmhsTransportFlow['layoutMode']>;
  foupReleaseMode: NonNullable<AmhsTransportFlow['foupReleaseMode']>;
  foupCountOverride: number | undefined;
  foupGapSec: number | undefined;
  hopGapSec: number | undefined;
  outsideStockerProcessWaitSec: number | undefined;
  inventoryEnabled: boolean;
  requireInventoryRouteTypes: string[] | undefined;
};

function readFlowOverrides(
  meta: Record<string, unknown> | null | undefined
): FlowOverrides {
  const flow =
    meta && typeof meta.transportFlow === 'object' && meta.transportFlow
      ? (meta.transportFlow as Record<string, unknown>)
      : {};
  const inventory =
    meta && typeof meta.inventory === 'object' && meta.inventory
      ? (meta.inventory as Record<string, unknown>)
      : {};

  const flowModeRaw = String(flow.flowMode ?? '').trim();
  const flowMode: FlowOverrides['flowMode'] =
    flowModeRaw === 'stocker_buffered' ||
    flowModeRaw === 'direct_tools' ||
    flowModeRaw === 'hybrid'
      ? flowModeRaw
      : TRANSPORT_FLOW_DEFAULTS.flowMode;

  const bayModeRaw = String(flow.bayMode ?? '').trim();
  const bayMode =
    bayModeRaw === 'browser_continuous' ? bayModeRaw : TRANSPORT_FLOW_DEFAULTS.bayMode;

  const layoutModeRaw = String(flow.layoutMode ?? '').trim();
  const layoutMode =
    layoutModeRaw === 'hybrid_e2e' ? layoutModeRaw : TRANSPORT_FLOW_DEFAULTS.layoutMode;

  const releaseRaw = String(flow.foupReleaseMode ?? '').trim().toLowerCase();
  // Keep in sync with backend transport_requests.serialize_foups aliases.
  const serialAliases = new Set([
    'serial',
    'one_by_one',
    'one-by-one',
    'sequential',
    'fifo_foup',
  ]);
  const segmentFreeAliases = new Set(['segment-free', 'segment_free']);
  const foupReleaseMode: FlowOverrides['foupReleaseMode'] =
    releaseRaw === 'auto' || releaseRaw === 'gap' || releaseRaw === 'manual'
      ? releaseRaw
      : segmentFreeAliases.has(releaseRaw)
        ? 'segment-free'
        : serialAliases.has(releaseRaw)
          ? 'serial'
          : TRANSPORT_FLOW_DEFAULTS.foupReleaseMode;

  const foupCountFromJson = Number(flow.foupCount);
  const foupGapSec = Number(flow.foupGapSec);
  const hopGapSec = Number(flow.hopGapSec);
  const outsideStockerProcessWaitSec = Number(flow.outsideStockerProcessWaitSec);

  // Master switch: inventory.enabled (preferred) or legacy requireInventoryBeforeProcess.
  // Default false — opt in for inventory wait/gating.
  let inventoryEnabled = false;
  if (typeof inventory.enabled === 'boolean') {
    inventoryEnabled = inventory.enabled;
  } else if (typeof inventory.requireInventoryBeforeProcess === 'boolean') {
    inventoryEnabled = inventory.requireInventoryBeforeProcess;
  } else if (typeof flow.inventoryEnabled === 'boolean') {
    inventoryEnabled = flow.inventoryEnabled;
  }

  const routeTypesRaw = inventory.requireInventoryRouteTypes;
  const requireInventoryRouteTypes = Array.isArray(routeTypesRaw)
    ? routeTypesRaw.map((r) => String(r).trim().toLowerCase()).filter(Boolean)
    : undefined;

  return {
    flowMode,
    bayMode,
    layoutMode,
    foupReleaseMode,
    foupCountOverride:
      Number.isFinite(foupCountFromJson) && foupCountFromJson > 0
        ? Math.floor(foupCountFromJson)
        : undefined,
    foupGapSec: Number.isFinite(foupGapSec) && foupGapSec >= 0 ? foupGapSec : undefined,
    hopGapSec: Number.isFinite(hopGapSec) && hopGapSec >= 0 ? hopGapSec : undefined,
    outsideStockerProcessWaitSec:
      Number.isFinite(outsideStockerProcessWaitSec) && outsideStockerProcessWaitSec >= 0
        ? outsideStockerProcessWaitSec
        : undefined,
    inventoryEnabled,
    requireInventoryRouteTypes,
  };
}

export function buildTransportFlowFromLayout(input: BuildTransportFlowInput): AmhsTransportFlow {
  const {
    stockers,
    tools,
    foupCount = DEFAULT_FOUP_COUNT,
    ohtCount,
    entryLandmarkId,
    exitLandmarkId,
    layoutMetadata,
  } = input;
  const overrides = readFlowOverrides(layoutMetadata);
  const resolvedFoupCount = overrides.foupCountOverride ?? foupCount;

  const outsideStocker = stockers.find((stocker) => stocker.outsideStocker) ?? stockers[0];
  const outsideStockerId = outsideStocker.stockerId;

  const toolsByType = groupToolsByType(tools);
  const processToolIndexById = buildProcessToolIndexByToolId(tools);

  const stages = Array.from(toolsByType.entries()).map(([toolType, groupTools]) => ({
    id: toolType.toLowerCase(),
    stockerId: stockerIdForToolType(stockers, toolType, outsideStockerId),
    toolType,
    toolCount: groupTools.length,
    toolOrder: groupTools.map((tool) => processToolIndexById.get(tool.toolId) ?? 1),
  }));

  return {
    version: 1,
    description: 'Layout-derived transport flow',
    outsideStockerId,
    ...(entryLandmarkId ? { entryLandmarkId } : {}),
    ...(exitLandmarkId ? { exitLandmarkId } : {}),
    flowMode: overrides.flowMode,
    bayMode: overrides.bayMode,
    layoutMode: overrides.layoutMode,
    foupCount: resolvedFoupCount,
    ohtCount,
    foupReleaseMode: overrides.foupReleaseMode,
    ...(overrides.foupGapSec != null ? { foupGapSec: overrides.foupGapSec } : {}),
    ...(overrides.hopGapSec != null ? { hopGapSec: overrides.hopGapSec } : {}),
    ...(overrides.outsideStockerProcessWaitSec != null
      ? { outsideStockerProcessWaitSec: overrides.outsideStockerProcessWaitSec }
      : {}),
    inventoryEnabled: overrides.inventoryEnabled,
    ...(overrides.inventoryEnabled &&
    overrides.requireInventoryRouteTypes &&
    overrides.requireInventoryRouteTypes.length > 0
      ? { requireInventoryRouteTypes: overrides.requireInventoryRouteTypes }
      : {}),
    stages,
  };
}
